import { useEffect, useRef, useState } from 'react';
import type { DateView, VenueId } from '@/api/types';
import { useConfig } from '@/api/queries';
import { cn } from '@/lib/cn';
import { venueOf } from './catalog';
import { useAmbiencePrefs, useThemeConsent, useVenueAmbience } from './useAmbience';
import { VenueFrame } from './VenueFrame';
import { VenueMark } from './VenueMark';
import { useStageVenue } from './stageVenue';

/** Venues are on unless the config says otherwise (older payloads omit it). */
export function useVenuesEnabled(): boolean {
  const config = useConfig();
  return config.data?.features.venues !== false;
}

function SpeakerIcon({ off }: { off: boolean }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 9v6h4l5 4V5L8 9Z" />
      {off ? (
        <path d="M17 9l5 6M22 9l-5 6" />
      ) : (
        <>
          <path d="M16.5 8.5a5 5 0 0 1 0 7" />
          <path d="M19 6a8.5 8.5 0 0 1 0 12" />
        </>
      )}
    </svg>
  );
}

/**
 * The ambience pill: venue + speaker. Tap to open mute and volume; the
 * sound itself never leaves this device.
 */
export function AmbienceControl({ venue, className }: { venue: VenueId; className?: string }) {
  const v = venueOf(venue)!;
  const { muted, volume, setMuted, setVolume } = useAmbiencePrefs();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('pointerdown', away);
    return () => window.removeEventListener('pointerdown', away);
  }, [open]);
  const off = muted || volume === 0;
  return (
    <div ref={ref} className={cn('pointer-events-auto relative', className)} onKeyDown={(e) => e.key === 'Escape' && setOpen(false)} data-tour="date-theme">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label={`${v.short} ambience: ${off ? 'off' : 'on'}`}
        className="inline-flex h-10 items-center gap-1.5 rounded-full bg-night/60 pl-1.5 pr-3 text-[13px] font-semibold text-bone transition-colors duration-150 hover:bg-night/75 focus-visible:outline focus-visible:outline-2 focus-visible:outline-bone"
        data-testid="ambience-control"
        data-muted={off}
      >
        <VenueMark venue={v.id} size={26} />
        <span className="max-w-[9rem] truncate">{v.short}</span>
        <span className={off ? 'opacity-60' : ''}>
          <SpeakerIcon off={off} />
        </span>
      </button>
      {open && (
        <div role="dialog" aria-label="Ambience" className="absolute left-0 top-12 z-30 w-64 rounded-card bg-surface p-4 text-fg shadow-lift" data-testid="ambience-panel">
          <p className="text-[15px] font-semibold">{v.name}</p>
          <p className="mt-0.5 text-[13px] text-muted">{v.sound}. Plays only on your device and dips while you talk.</p>
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={() => setMuted(!muted)}
              aria-pressed={off}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-raised hover:bg-line focus-visible:outline focus-visible:outline-2 focus-visible:outline-fg"
              aria-label={off ? 'Turn ambience on' : 'Mute ambience'}
              data-testid="ambience-mute"
            >
              <SpeakerIcon off={off} />
            </button>
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={Math.round((muted ? 0 : volume) * 100)}
              onChange={(e) => {
                const x = Number(e.target.value) / 100;
                setVolume(x);
                if (x > 0 && muted) setMuted(false);
              }}
              aria-label="Ambience volume"
              className="w-full"
              data-testid="ambience-volume"
            />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * v14: "Start the theme?" — asked once per date before any frame or sound.
 * Not now leaves a small pill to start it later.
 */
export function ThemePrompt({ venue, consent, onAnswer, className }: { venue: VenueId; consent: 'off' | undefined; onAnswer: (c: 'on' | 'off') => void; className?: string }) {
  const v = venueOf(venue)!;
  if (consent === 'off') {
    return (
      <div className={cn('pointer-events-auto', className)} data-tour="date-theme">
        <button
          type="button"
          onClick={() => onAnswer('on')}
          className="inline-flex h-10 items-center gap-1.5 rounded-full bg-night/60 pl-1.5 pr-3 text-[13px] font-semibold text-bone transition-colors duration-150 hover:bg-night/75 focus-visible:outline focus-visible:outline-2 focus-visible:outline-bone"
          data-testid="theme-start-later"
        >
          <VenueMark venue={v.id} size={26} />
          Start the {v.short} theme
        </button>
      </div>
    );
  }
  return (
    <div role="dialog" aria-label={`Start the ${v.short} theme?`} className={cn('pointer-events-auto w-[min(18rem,calc(100vw-1.5rem))] rounded-card bg-surface p-4 text-fg shadow-lift', className)} data-testid="theme-prompt" data-venue={v.id} data-tour="date-theme">
      <div className="flex items-center gap-3">
        <VenueMark venue={v.id} size={36} />
        <div className="min-w-0">
          <p className="text-[15px] font-semibold leading-tight">Start the {v.short} theme?</p>
          <p className="mt-0.5 text-[13px] text-muted">{v.sound}, and a frame around the video. Only on your device.</p>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <button type="button" onClick={() => onAnswer('off')} className="h-10 rounded-full bg-raised text-[15px] font-semibold hover:bg-line focus-visible:outline focus-visible:outline-2 focus-visible:outline-fg" data-testid="theme-no">
          Not now
        </button>
        <button type="button" onClick={() => onAnswer('on')} className="h-10 rounded-full bg-primary text-[15px] font-semibold text-ground focus-visible:outline focus-visible:outline-2 focus-visible:outline-fg" data-testid="theme-yes">
          Start it
        </button>
      </div>
    </div>
  );
}

/**
 * The venue on the call stage (SPEC_V9 §B1): the decorative frame, the
 * ambience (while the date runs) and its control. Mount inside the stage.
 * v14: nothing starts until you say yes to the theme (once per date).
 */
export function VenueStage({ date, speaking, controlClassName }: { date: DateView | null; speaking: boolean; controlClassName?: string }) {
  const on = useVenuesEnabled();
  const consent = useThemeConsent((s) => (date ? s.byDate[date.id] : undefined));
  const setConsent = useThemeConsent((s) => s.set);
  // Only catalog venues (a newer server's unknown id reads as none).
  const venue = on && date ? (venueOf(date.venue)?.id ?? null) : null;
  const live = !!venue && !!date && date.status !== 'ended';
  const started = consent === 'on';
  const engine = useVenueAmbience(venue, { enabled: live && started, speaking });
  // v16: tell the sheets which place is live (and how to swell its sound).
  const setStage = useStageVenue((s) => s.set);
  const onStage = live && started ? venue : null;
  useEffect(() => {
    setStage(onStage, () => engine.swell());
    return () => setStage(null);
  }, [onStage, engine, setStage]);
  if (!venue || !date) return null;
  const place = controlClassName ?? 'left-4 top-4';
  const at = cn('absolute z-20', place);
  // The question sits above the self-view (also z-20) so nothing covers its buttons.
  if (!started) return live ? <ThemePrompt venue={venue} consent={consent} onAnswer={(c) => setConsent(date.id, c)} className={cn('absolute z-[35]', place)} /> : null;
  return (
    <>
      <VenueFrame venue={venue} className="z-[5]" />
      {live && <AmbienceControl venue={venue} className={at} />}
    </>
  );
}
